import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';

function Login() {
  const { user, loading, signIn, callbackMessage, isDevAuthBypass } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = useMemo(() => location.state?.from?.pathname || '/print-studio', [location.state]);

  useEffect(() => {
    if (user && !loading) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  return (
    <Page>
      <Card>
        <Eyebrow>Private access</Eyebrow>
        <h1>Sign in to Print Studio</h1>
        <p className="lede">Use your invited email address and the password you created from the invite link.</p>
        {isDevAuthBypass ? <Notice>Local development bypass is enabled, so you can sign in with any email and password while testing on localhost.</Notice> : null}

        {callbackMessage ? <Notice>{callbackMessage}</Notice> : null}
        {error ? <ErrorBox>{error}</ErrorBox> : null}

        <form onSubmit={handleSubmit}>
          <label>
            Email
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            Password
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <button type="submit" disabled={busy}>
            {busy ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <FooterText>
          Need access? Ask the site owner for an invitation. <Link to="/">Return home</Link>
        </FooterText>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 32px;
  background: radial-gradient(circle at top, #2a1f35 0%, #191220 55%, #120c18 100%);
  color: #f4e8f2;
`;

const Card = styled.div`
  width: min(100%, 460px);
  background: rgba(25, 18, 32, 0.9);
  border: 1px solid rgba(224, 182, 220, 0.16);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);

  h1 {
    margin: 8px 0 12px;
    font-size: 2rem;
  }

  .lede {
    margin: 0 0 20px;
    color: rgba(244, 232, 242, 0.8);
    line-height: 1.5;
  }

  form {
    display: grid;
    gap: 16px;
    margin-top: 12px;
  }

  label {
    display: grid;
    gap: 8px;
    font-size: 0.95rem;
    text-align: left;
  }

  input {
    width: 100%;
    border: 1px solid rgba(224, 182, 220, 0.25);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    padding: 14px 16px;
    font-size: 1rem;
    outline: none;
  }

  input:focus {
    border-color: #e0b6dc;
    box-shadow: 0 0 0 3px rgba(224, 182, 220, 0.15);
  }

  button {
    border: none;
    border-radius: 14px;
    padding: 14px 18px;
    background: #e0b6dc;
    color: #191220;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  button:hover {
    transform: translateY(-1px);
  }

  button:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #c98bc1;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.8rem;
  font-weight: 700;
`;

const Notice = styled.div`
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(131, 146, 231, 0.12);
  border: 1px solid rgba(131, 146, 231, 0.24);
  color: #dce4ff;
  text-align: left;
`;

const ErrorBox = styled(Notice)`
  background: rgba(255, 99, 132, 0.12);
  border-color: rgba(255, 99, 132, 0.24);
  color: #ffd5df;
`;

const FooterText = styled.p`
  margin-top: 20px;
  color: rgba(244, 232, 242, 0.75);

  a {
    color: #8392e7;
    text-decoration: none;
  }
`;

export default Login;
